/** @jsxImportSource @emotion/react */
import { css } from "@emotion/react";

import { useEffect } from "react";

import { useDetailsStore } from "@/stores/useDetailsStore";
import { useTableStoreByRoute } from "@/stores/useTableStore";

import { colors } from "@dotnet/telematics-design-system";

import {
  desktopDetailsSectionWidth,
  ROUTES,
  TUTORIAL_IDS,
  useMediaQueries
} from "../../../../shared";
import useTutorialMode from "../../../TutorialMode/hooks/useTutorialMode";
import { STEP_GROUP } from "../../../TutorialMode/types";
import {
  SECTION_OPEN_DELAY_NOT_RESET,
  SECTION_OPEN_DELAY_ON_RESET
} from "../../constants";
import { useStyles } from "../../styles";
import DetailsContent from "../DetailsContent/DetailsContent";

interface DesktopDetailsProps {
  route: ROUTES;
}

const DesktopDetails = ({ route }: DesktopDetailsProps) => {
  const { toLg } = useMediaQueries();

  const {
    stepGroup,
    stepIndex,
    callTutorialWithTimeout,
    checkIfCorrectTutorialStep
  } = useTutorialMode();

  const {
    initialLoading,
    data: tableRowData,
    params,
    setParams,
    setDetailsData
  } = useTableStoreByRoute(route);

  const isDetailsOpen = params.desktopDetailsSection;

  const { resetDetailsSection, setResetDetailsSection } = useDetailsStore();

  const { desktopDetailsAside, detailsContentContainer } = useStyles();

  useEffect(() => {
    if (isDetailsOpen && tableRowData.length === 0 && !initialLoading) {
      handleCloseDesktopDetails();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tableRowData]);

  useEffect(() => {
    if (isDetailsOpen) {
      if (checkIfCorrectTutorialStep(STEP_GROUP.TutorialNewCompanyCard, 3)) {
        callTutorialWithTimeout(true, stepIndex, stepGroup, true, 500);
      }
      if (checkIfCorrectTutorialStep(STEP_GROUP.TutorialApplySchedule, 5)) {
        callTutorialWithTimeout(true, stepIndex, stepGroup, true, 500);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDetailsOpen, stepIndex]);

  const delay =
    resetDetailsSection && isDetailsOpen
      ? SECTION_OPEN_DELAY_ON_RESET
      : SECTION_OPEN_DELAY_NOT_RESET;

  const handleCloseDesktopDetails = () => {
    setParams({
      desktopDetailsSection: false,
      selectedTableItem: null
    });
    setDetailsData(null);
    setResetDetailsSection(false);
  };

  return (
    <aside
      data-testid={TUTORIAL_IDS.DesktopDetailsAside}
      id={TUTORIAL_IDS.DesktopDetailsAside}
      css={css([
        {
          width:
            isDetailsOpen && tableRowData.length > 0
              ? desktopDetailsSectionWidth(toLg)
              : "0",
          transition: `width 0.3s cubic-bezier(0.4, 0, 0.2, 1) ${delay}`,
          borderLeft: `1px solid var(--Gray-Gray200, ${colors.gray200})`
        },
        desktopDetailsAside
      ])}
    >
      <div
        css={css([
          {
            height: `100%`
          },
          detailsContentContainer
        ])}
      >
        {isDetailsOpen && tableRowData.length > 0 && (
          <DetailsContent route={route} onClose={handleCloseDesktopDetails} />
        )}
      </div>
    </aside>
  );
};

export default DesktopDetails;
